import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { getPinnedConversationLimitErrorData } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { type Conversation } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { setConversationData } from './setConversationData';
import type { ChatStoreAction } from './types';

export const updateConversation =
	({
		conversationId,
		data,
	}: {
		conversationId: string;
		data: Partial<Pick<Conversation, 'name' | 'readStatus' | 'isPinned' | 'pinnedAt'>>;
	}): ChatStoreAction =>
	async ({ dispatch }, { assistanceService, experienceTracker }) => {
		experienceTracker.start({
			name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
			id: conversationId,
			attributes: {
				conversationId,
			},
		});

		let updatedConversation;
		try {
			updatedConversation = await assistanceService?.updateConversationChannel(
				conversationId,
				data,
			);
		} catch (error: unknown) {
			const pinLimitErrorData = data.isPinned
				? getPinnedConversationLimitErrorData(error)
				: undefined;
			if (pinLimitErrorData !== undefined) {
				experienceTracker.abort({
					name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
					reason: 'pinned_conversation_limit_reached',
					attributes: {
						conversationId,
						...pinLimitErrorData,
					},
				});
			} else {
				experienceTracker.fail({
					name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
					error: error instanceof Error ? error : new Error(String(error)),
				});
			}

			throw error;
		}

		const nextData: Partial<Conversation> = { ...data };
		if (updatedConversation) {
			if (
				fg('rovo_chat_chat_history_refresh') &&
				updatedConversation.agentSessionState !== undefined
			) {
				nextData.agentSessionState = updatedConversation.agentSessionState;
			}
			if (updatedConversation.name !== undefined) {
				nextData.name = updatedConversation.name;
			}
			if (updatedConversation.readStatus !== undefined) {
				nextData.readStatus = updatedConversation.readStatus;
			}
			if (typeof updatedConversation.isPinned === 'boolean') {
				nextData.isPinned = updatedConversation.isPinned;
			}
			if (updatedConversation.pinnedAt !== undefined) {
				nextData.pinnedAt = updatedConversation.pinnedAt;
			}
		}

		dispatch(
			setConversationData({
				conversationId,
				data: nextData,
			}),
		);

		experienceTracker.succeed({
			name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
		});
	};
